import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { useTranslation } from "@i18n";
import axiosWrapper from "@Services/axiosWrapper";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import { QueryBuilder, PredicateBuilder } from "@Services/queryBuilder";
import Views, { CREATE_LAYOUT_OPTION } from "@Components/Views";
import ApplyScopeModal from "./ApplyScopeModal";
import ConfigureLayoutModal from "./ConfigureLayoutModal";
import {
  PROJECT_DASHBOARD_RESOURCE_TYPE,
  PROJECT_DASHBOARD_SELECTED_LAYOUT_KEY,
  LAYOUT_MENU_ACTION,
  ALL_MODULES_LAYOUT_CODE,
  SIDEBAR_LAYOUTS_TOGGLE_ATTR,
  getLayoutSelection,
  getLayoutViewKey,
} from "@Helpers/constants";
import type { LayoutData } from "@ServerTypes/layoutView/layoutView.schema";
import type { DropdownItem } from "@Components/Views/types";
import type { SidebarIcon } from "./ConfigureLayoutModal";
import { layoutKind } from "@Features/projects/utils/layoutKind";
import { nextAvailableLayoutName } from "@Features/projects/utils/layoutNameValidation";
import { useSidebarLayoutPermissions } from "@Features/projects/utils/useSidebarLayoutPermissions";

interface ProjectLayoutsProps {
  onClose: () => void;
  /** "mid" position sidebar icons, used as the pool of available modules in the create modal. */
  midIcons: SidebarIcon[];
  projectId: string | null;
  /** Whether the sidebar is expanded — used to anchor the flyout just to its right. */
  isExpanded: boolean;
  /** Key of the currently applied layout — its `_id`, or `code` for a system layout (for highlighting in the list). */
  selectedLayoutId?: string | undefined;
  /** Called when a layout becomes active (clicked or just created) so the sidebar can apply it. */
  onLayoutSelected: (layout: LayoutData) => void;
}

function ProjectLayouts({
  onClose,
  midIcons,
  projectId,
  isExpanded,
  selectedLayoutId,
  onLayoutSelected,
}: ProjectLayoutsProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const permissions = useSidebarLayoutPermissions();
  const flyoutRef = useRef<HTMLDivElement>(null);
  const [layoutData, setLayoutData] = useState<LayoutData[]>([]);
  // Distinguishes "fetch in flight" from "there are no layouts", which `Views` would otherwise both
  // render as its "No Data" empty state. Starts true unconditionally: the fetch below is portal-level
  // and runs on mount regardless of whether a project is open.
  const [isLayoutsLoading, setIsLayoutsLoading] = useState<boolean>(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createShared, setCreateShared] = useState(false);
  // The layout currently being configured (edit mode); null when creating / closed.
  const [editLayout, setEditLayout] = useState<LayoutData | null>(null);
  // The layout awaiting an apply-scope answer; null when the dialog is closed.
  const [scopePrompt, setScopePrompt] = useState<LayoutData | null>(null);

  const isModalOpen = showCreateModal || editLayout !== null || scopePrompt !== null;

  // NOTE: there is deliberately no re-arm-on-projectId latch here. One existed while layouts were
  // fetched per project, to stop the previous project's list being painted as the new one's. Layouts
  // are portal-level now — the same list is correct in every project and is never refetched — so
  // re-arming the loading flag on a project switch would spin forever with nothing to clear it.

  // Sidebar layouts are PORTAL-level: one document, available in every project. So the fetch sends no
  // projectId (which drops it into getViewData's portal-level arm) and runs once, rather than
  // re-running per project. That also removes the stale-response race the old per-project fetch had.
  useEffect(() => {
    const loadLayouts = async () => {
      try {
        const query = new QueryBuilder().where(
          new PredicateBuilder("resourceType", "equal", PROJECT_DASHBOARD_RESOURCE_TYPE),
        );
        const response = await axiosWrapper.api.post("/layoutView/getViewData", {
          value: query.build(),
        });
        if (response.data.status) setLayoutData(response.data.result ?? []);
      } catch {
        // Leave the current list on a transient fetch failure; the flyout is a secondary surface and
        // the original path already ignored non-success responses silently.
      } finally {
        // Unconditional: the fetch has no stale-response guard to consult because it runs exactly
        // once on mount. (A `active` flag here was left over from the per-project fetch and threw a
        // ReferenceError, which is what kept the spinner up forever.)
        setIsLayoutsLoading(false);
      }
    };
    void loadLayouts();
  }, []);

  // Close the flyout on an outside click. A document listener (instead of a full-screen
  // overlay) avoids intercepting scroll/pointer events over the dashboard behind it.
  useEffect(() => {
    // While a modal is open, let it own dismissal — don't close the flyout underneath it.
    if (isModalOpen) return;
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      // Click inside the flyout itself — keep open.
      if (flyoutRef.current && flyoutRef.current.contains(target)) return;
      // The Layouts sidebar icon owns its own toggle (open/close). Let its onClick handle it,
      // otherwise this mousedown closes the flyout right before the toggle reopens it.
      if (target.closest(`[${SIDEBAR_LAYOUTS_TOGGLE_ATTR}]`)) return;
      // The row menus and the create menu render as MUI Poppers, and modals as MUI Dialogs,
      if (target.closest(".MuiPopper-root, .MuiModal-root, .MuiDialog-root, .MuiBackdrop-root")) return;
      onClose();
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [isModalOpen, onClose]);

  /**
   * Persists the selection and applies it to the sidebar.
   *
   * Omitting `projectId` is the ENTIRE client side of "apply for all projects": the server then
   * upserts the caller's single portal-wide selection row and clears their per-project overrides,
   * instead of writing a row scoped to this project.
   */
  const commitApply = useCallback(
    async (layout: LayoutData, { applyToAllProjects = false } = {}) => {
      if (!getLayoutViewKey(layout)) return;
      onLayoutSelected(layout);
      // A this-project selection is scoped to the open project; with none open there is no project to
      // persist it under, so it stays a choice for this session.
      if (!applyToAllProjects && projectId === null) return;
      try {
        await axiosWrapper.api.post("/layoutView/setSelectedViewerLayout", {
          ...getLayoutSelection(layout),
          key: PROJECT_DASHBOARD_SELECTED_LAYOUT_KEY,
          ...(!applyToAllProjects && projectId !== null && { projectId }),
          applyToAllProjects,
        });
        dispatch(showToastNotification({ content: t("Layout applied successfully"), type: TOAST_TYPE_SUCCESS }));
      } catch {
        // The layout is already applied in the UI; only persistence failed — tell the user so it isn't
        // a silent no-op that reverts on the next reload.
        dispatch(showToastNotification({ content: t("Something went wrong"), type: TOAST_TYPE_ERROR }));
      }
    },
    [dispatch, onLayoutSelected, projectId, t],
  );

  const closeModal = () => {
    setShowCreateModal(false);
    setEditLayout(null);
  };

  /**
   * Per-row ••• menu.
   *
   * `createdBy` is never consulted — that is the whole point of the permission model. Rights over a
   * SHARED layout come only from EDIT_SHARED_LAYOUT / DELETE_SHARED_LAYOUT, so an Edit holder may
   * configure or rename anyone's, and a creator without Edit cannot touch even their own. PERSONAL
   * layouts sit outside the permissions entirely (the list only ever contains the caller's own), and
   * SYSTEM layouts are immutable for everyone — their items are ABSENT rather than disabled, which is
   * also why the in-use delete block can never apply to one.
   *
   * There is no "Apply" item: clicking the row applies the layout. There is no "Rename" item
   * either — Configure opens with the name field, so renaming happens there.
   */
  const getDropDownItems = useCallback(
    (item: LayoutData): DropdownItem[] => {
      const configure = { text: t("Configure"), id: LAYOUT_MENU_ACTION.EDIT, iconName: "SettingsIcon" };
      const duplicate = { text: t("Duplicate"), id: LAYOUT_MENU_ACTION.DUPLICATE, iconName: "copy" };
      const duplicateAsMy = {
        text: t("Duplicate to My Layouts"),
        id: LAYOUT_MENU_ACTION.DUPLICATE_AS_MY,
        iconName: "copy",
      };
      const moveToShared = {
        text: t("Move to Shared Layout"),
        id: LAYOUT_MENU_ACTION.MOVE_TO_SHARED,
        iconName: "copy",
      };
      const remove = { text: t("Delete"), id: LAYOUT_MENU_ACTION.DELETE, iconName: "delete" };

      const kind = layoutKind(item);
      if (kind === "system") {
        // Duplicate is the ONLY action, for every user including Super Admin.
        return [duplicate];
      }
      if (kind === "personal") {
        return [configure, duplicate, ...(permissions.canCreateShared ? [moveToShared] : []), remove];
      }
      // Shared. "Duplicate to My Layouts" needs no permission — taking a personal copy is always allowed.
      return [
        duplicateAsMy,
        ...(permissions.canCreateShared ? [duplicate] : []),
        ...(permissions.canEditShared ? [configure] : []),
        ...(permissions.canDeleteShared ? [remove] : []),
      ];
    },
    [permissions, t],
  );

  /** Publishing to the whole portal is gated; creating a personal layout never is. */
  const createOptions = useMemo(
    () => [
      { text: t("My Layout"), id: CREATE_LAYOUT_OPTION.MY },
      ...(permissions.canCreateShared ? [{ text: t("Shared Layout"), id: CREATE_LAYOUT_OPTION.SHARED }] : []),
    ],
    [permissions.canCreateShared, t],
  );

  /**
   * Names a duplicate. Required because the server enforces per-namespace name uniqueness, so
   * copying the source name verbatim would always collide with the row being copied — and
   * "Duplicate to My Layouts" on a shared "All Modules" would try to mint a name that is reserved
   * in the personal namespace.
   */
  const getDuplicateName = useCallback(
    (source: LayoutData, isShared: boolean): string =>
      nextAvailableLayoutName({ baseName: source.name ?? t("Layout Name"), isShared, layouts: layoutData }),
    [layoutData, t],
  );

  /**
   * A layout was created or reconfigured.
   *
   * Create chains into the apply-scope dialog — the layout is created FIRST and the scope asked
   * second. Configure-save does not: it shows a plain success toast (no affected-user counts) and,
   * when the edited layout is the applied one, silently re-applies under the scope already in effect.
   */
  const handleSaved = (layout: LayoutData, isEdit: boolean) => {
    if (isEdit) {
      setLayoutData((previous) => previous.map((item) => (item._id === layout._id ? { ...item, ...layout } : item)));
      if (getLayoutViewKey(layout) === selectedLayoutId) {
        // Fire-and-forget: handleSaved is synchronous and the modal close below must not wait on the
        // re-apply, which persists the selection on its own.
        void commitApply(layout);
      }
      closeModal();
      return;
    }
    setLayoutData((previous) => [...previous, layout]);
    closeModal();
    setScopePrompt(layout);
  };

  return (
    <>
      <div
        ref={flyoutRef}
        className="fixed bottom-4 z-1201 flex h-124 max-h-[calc(100vh-2rem)] w-126 flex-col overflow-auto rounded-lg bg-white p-6 text-(--primary-text) shadow-[0_0_0.9375rem_0_rgba(65,92,171,0.25)]"
        style={{ insetInlineStart: isExpanded ? "16rem" : "5rem" }}
        data-testid="layoutsFlyout"
      >
        <Views
          layoutData={layoutData}
          setLayoutData={setLayoutData}
          isLoading={isLayoutsLoading}
          // `currentDataId` is `?: string` on `Views`, so under `exactOptionalPropertyTypes` a
          // flyout opened before any layout is selected omits the prop rather than passing
          // `undefined`; `Views` compares it by equality either way.
          {...(selectedLayoutId !== undefined && { currentDataId: selectedLayoutId })}
          getDropDownItems={getDropDownItems}
          createOptions={createOptions}
          getDuplicateName={getDuplicateName}
          onEditCallback={({ layoutData: layoutToConfigure }: { layoutData: LayoutData }) =>
            setEditLayout(layoutToConfigure)
          }
          onClickLayout={(layout: LayoutData) => setScopePrompt(layout)}
          // Deleting the active layout would leave redux + user_settings pointing at a now soft-deleted
          // row. Re-point the selection to the "All Modules" default so nothing dangles.
          onDeleteCallback={({ deleteData }: { deleteData: LayoutData }) => {
            // `Views` reports only FAILED deletes; success feedback is the caller's, which is how the
            // design viewer does it too (its own onDeleteCallback toasts). Without this the row just
            // disappeared with no confirmation.
            dispatch(showToastNotification({ content: t("Layout deleted successfully"), type: TOAST_TYPE_SUCCESS }));
            if (getLayoutViewKey(deleteData) !== selectedLayoutId) return;
            const allModules = layoutData.find((layout) => layout.code === ALL_MODULES_LAYOUT_CODE);
            if (allModules) void commitApply(allModules);
          }}
          createNewLayoutData={() => ({})}
          onCreateClick={(isShared) => {
            setCreateShared(isShared);
            setShowCreateModal(true);
          }}
        />
      </div>
      {(showCreateModal || editLayout !== null) && (
        <ConfigureLayoutModal
          midIcons={midIcons}
          isShared={createShared}
          layouts={layoutData}
          // `layoutToEdit` is `?: LayoutData`; "create" mode omits it instead of passing `undefined`,
          // which is what `editLayout === null` already meant to the modal.
          {...(editLayout != null && { layoutToEdit: editLayout })}
          onClose={closeModal}
          onSaved={handleSaved}
        />
      )}
      {scopePrompt !== null && (
        <ApplyScopeModal
          layoutName={scopePrompt.name ?? ""}
          onCancel={() => setScopePrompt(null)}
          onConfirm={({ applyToAllProjects }) => {
            const layout = scopePrompt;
            setScopePrompt(null);
            void commitApply(layout, { applyToAllProjects });
            onClose();
          }}
        />
      )}
    </>
  );
}

export default ProjectLayouts;
